import { lazy, Suspense, Component } from 'react';
import { useAuth } from './context/AuthContext';

// Catches the stale-bundle case where a code-split chunk is missing on
// the CDN (typical after a deploy lands while a tab is still open). On
// any ChunkLoadError, force a one-time hard reload to fetch the new
// asset manifest. Reload guard prevents infinite loops on genuine CDN
// outages.
const RELOAD_KEY = 'propsight:chunk-reload-at';
class ChunkBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error) {
    const isChunkError = error?.name === 'ChunkLoadError' || /Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(error?.message || '');
    if (!isChunkError) return;
    const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - lastReload < 10_000) return;  // already tried recently — let the error show
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', background: '#0B1220', color: '#F1F5F9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'system-ui', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Something went wrong loading the app.</div>
          <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 18 }}>A newer version may have shipped. Reload to pick it up.</div>
          <button onClick={() => { sessionStorage.removeItem(RELOAD_KEY); window.location.reload(); }} style={{ padding: '10px 22px', borderRadius: 10, background: '#38BDF8', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Code-split every top-level surface. Visitors only download the chunks
// they actually need (e.g. a logged-out landing-page hit doesn't fetch
// the broker-portal bundle). Heavy in-app pages then arrive on demand.
const LoginPage         = lazy(() => import('./components/LoginPage'));
const LandingPage       = lazy(() => import('./components/LandingPage'));
const InvestorApp       = lazy(() => import('./components/InvestorApp'));
const OnboardingPage    = lazy(() => import('./components/OnboardingPage'));
const BrokerApp         = lazy(() => import('./components/BrokerApp'));
const AgencyApp         = lazy(() => import('./components/AgencyApp'));
const AdminApp          = lazy(() => import('./components/AdminApp'));
import { isAdminEmail } from './utils/admin';
const PublicReport      = lazy(() => import('./components/PublicReport'));
const PublicBrokerPage  = lazy(() => import('./components/PublicBrokerPage'));
const PublicAreaPage    = lazy(() => import('./components/PublicAreaPage'));
const DealShareView     = lazy(() => import('./components/DealShareView'));
const InviteAcceptPage  = lazy(() => import('./components/InviteAcceptPage'));
const ContactPage       = lazy(() => import('./components/ContactPage'));
const DemoExpiredOverlay = lazy(() => import('./components/DemoExpiredOverlay'));
const DemoTrialBanner    = lazy(() => import('./components/DemoTrialBanner'));
const PWAInstallPrompt   = lazy(() => import('./components/PWAInstallPrompt'));

// Wraps the active portal for demo callers: thin trial-countdown banner
// across the top while the trial is live, and the full-page overlay once
// it's expired. No-op for real users.
function WithDemoOverlay({ children }) {
  const { isDemo, demoTrial } = useAuth();
  const expired = isDemo && demoTrial?.expired;
  return (
    <>
      {isDemo && !expired && (
        <Suspense fallback={null}><DemoTrialBanner /></Suspense>
      )}
      {children}
      {expired && (
        <Suspense fallback={null}><DemoExpiredOverlay /></Suspense>
      )}
      {/* PWA install prompt — only meaningful inside authenticated
          portals (public share pages don't need install nudges). Lazy-
          loaded + auto-hidden when running in standalone mode. */}
      <Suspense fallback={null}><PWAInstallPrompt /></Suspense>
    </>
  );
}

function RouteFallback() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg, #0B1220)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        border: '2px solid rgba(255,255,255,0.08)',
        borderTopColor: '#38BDF8',
        animation: 'psSpin 0.8s linear infinite',
      }} />
      <style>{`@keyframes psSpin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

export default function App() {
  return (
    <ChunkBoundary>
      <AppInner />
    </ChunkBoundary>
  );
}

function AppInner() {
  const { user, profile, loading } = useAuth();

  // Public broker profile — bypasses auth so anyone can view shared links.
  const brokerSlugMatch = window.location.pathname.match(/^\/broker\/([a-z0-9-]+)\/?$/i);
  if (brokerSlugMatch) {
    return <Suspense fallback={<RouteFallback />}><PublicBrokerPage slug={brokerSlugMatch[1].toLowerCase()} /></Suspense>;
  }

  // Public area page — indexable, no auth required. Powers SEO acquisition.
  const areaSlugMatch = window.location.pathname.match(/^\/area\/([a-z0-9-]+)\/?$/i);
  if (areaSlugMatch) {
    return <Suspense fallback={<RouteFallback />}><PublicAreaPage slug={areaSlugMatch[1].toLowerCase()} /></Suspense>;
  }

  // Public deal share — WhatsApp landing page. No auth. Broker-branded.
  const dealSlugMatch = window.location.pathname.match(/^\/deal\/([a-zA-Z0-9_-]+)\/?$/);
  if (dealSlugMatch) {
    return <Suspense fallback={<RouteFallback />}><DealShareView slug={dealSlugMatch[1]} /></Suspense>;
  }

  // Agency invite acceptance.
  const inviteMatch = window.location.pathname.match(/^\/invite\/([a-f0-9]+)\/?$/i);
  if (inviteMatch) {
    return <Suspense fallback={<RouteFallback />}><InviteAcceptPage token={inviteMatch[1].toLowerCase()} /></Suspense>;
  }

  // Public contact pages — auth-agnostic so logged-in users can also reach
  // them (e.g. to upgrade their account to an agency plan).
  if (/^\/contact\/agency\/?$/i.test(window.location.pathname)) {
    return <Suspense fallback={<RouteFallback />}><ContactPage variant="agency" /></Suspense>;
  }
  if (/^\/contact\/?$/i.test(window.location.pathname)) {
    return <Suspense fallback={<RouteFallback />}><ContactPage variant="regular" /></Suspense>;
  }

  // Public shareable report — bypasses auth so anyone with the link can view.
  const sharedReport = new URLSearchParams(window.location.search).get('report');
  if (sharedReport) {
    return <Suspense fallback={<RouteFallback />}><PublicReport encoded={sharedReport} /></Suspense>;
  }

  // Still loading auth state
  if (loading) return <RouteFallback />;

  // New user — needs onboarding
  if (user && profile && profile.onboarded === false) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <OnboardingPage user={user} onComplete={() => window.location.reload()} />
      </Suspense>
    );
  }

  // Not logged in — show landing or login
  if (!user) {
    const showLogin = sessionStorage.getItem('show_login');
    if (showLogin) return <Suspense fallback={<RouteFallback />}><LoginPage /></Suspense>;
    return (
      <Suspense fallback={<RouteFallback />}>
        <LandingPage
          onLogin={() => { sessionStorage.setItem('show_login','1'); window.location.reload(); }}
          onInvestorLogin={() => { sessionStorage.setItem('show_login','1'); sessionStorage.setItem('intended_role','investor'); window.location.reload(); }}
          onBrokerLogin={() => { sessionStorage.setItem('show_login','1'); sessionStorage.setItem('intended_role','broker'); window.location.reload(); }}
        />
      </Suspense>
    );
  }

  const params = new URLSearchParams(window.location.search);
  const portalParam = params.get('portal');
  const isAdmin = isAdminEmail(user.email);
  const isDemoProfile = !!profile?.is_demo;

  // Demo accounts are scoped to the broker portal — kill any sticky
  // `preferred_portal=agency` so a forced ?portal=agency URL can't trap
  // them in an Agency → redirect → Agency reload loop.
  if (isDemoProfile && localStorage.getItem('preferred_portal') === 'agency') {
    localStorage.removeItem('preferred_portal');
  }

  // Admin routing — admins see whatever portal the URL asks for, no role or
  // org checks. ?portal=broker/investor/agency forces that view; no override
  // lands on AdminApp. Done before the regular routing so an admin with
  // profile.role='broker' doesn't get short-circuited into BrokerApp when
  // they ask for ?portal=investor.
  if (isAdmin) {
    if (portalParam === 'broker' || portalParam === 'investor' || portalParam === 'agency') {
      const Portal = portalParam === 'broker' ? BrokerApp
                    : portalParam === 'investor' ? InvestorApp
                    : AgencyApp;
      return (
        <WithDemoOverlay>
          <Suspense fallback={<RouteFallback />}>
            <Portal />
          </Suspense>
        </WithDemoOverlay>
      );
    }
    return (
      <WithDemoOverlay>
        <Suspense fallback={<RouteFallback />}>
          <AdminApp />
        </Suspense>
      </WithDemoOverlay>
    );
  }

  // Non-admin agency portal — opt-in via ?portal=agency (set by
  // PortalSwitcher) or a sticky preference once the user has crossed over.
  // Requires active org membership; falls through to broker/investor
  // routing otherwise. Admin path above skips this so the sticky preference
  // doesn't trap them on AgencyApp after one preview visit.
  if (portalParam === 'agency' && !isDemoProfile) localStorage.setItem('preferred_portal', 'agency');
  if (portalParam === 'broker' || portalParam === 'investor') localStorage.removeItem('preferred_portal');
  const wantsAgency = !isDemoProfile && (portalParam === 'agency' || localStorage.getItem('preferred_portal') === 'agency');
  if (wantsAgency) {
    return (
      <WithDemoOverlay>
        <Suspense fallback={<RouteFallback />}>
          <AgencyApp />
        </Suspense>
      </WithDemoOverlay>
    );
  }

  // Broker portal — only for verified brokers. Anyone else (including a
  // user who self-selected 'broker' but hasn't been verified) gets the
  // investor app and the "Apply for Broker Access" KYC flow.
  const isVerifiedBroker = profile?.role === 'broker' && profile?.broker_verified === true;
  return (
    <WithDemoOverlay>
      <Suspense fallback={<RouteFallback />}>
        {isVerifiedBroker ? <BrokerApp /> : <InvestorApp />}
      </Suspense>
    </WithDemoOverlay>
  );
}
